import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Users, MessageCircleHeart, TrendingUp, BookHeart, Sparkles, Crown } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

const Stat = ({ icon: Icon, label, value, sub }) => (
    <div className="bg-card rounded-3xl p-6 border border-border shadow-soft" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex items-center gap-2 mb-3">
            <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{label}</span>
        </div>
        <div className="font-heading text-3xl font-[800] tracking-tight">{value ?? "—"}</div>
        {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </div>
);

export default function Admin() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user && !user.is_admin) {
            toast.error("Admins only");
            navigate("/app", { replace: true });
            return;
        }
        (async () => {
            try {
                const [s, u] = await Promise.all([
                    api.get("/admin/stats"),
                    api.get("/admin/users?limit=50"),
                ]);
                setStats(s.data);
                setUsers(u.data.users || []);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not load admin data");
            } finally {
                setLoading(false);
            }
        })();
    }, [user, navigate]);

    if (loading) {
        return <div className="p-10 text-muted-foreground">Loading admin data…</div>;
    }
    if (!stats) return null;

    const chartData = (stats.signups_by_day || []).map(d => ({
        date: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        signups: d.count,
    }));

    return (
        <div className="max-w-6xl mx-auto p-6 sm:p-10 lg:p-14">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-4">
                    <ShieldCheck className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Superuser view</span>
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight">
                    Admin dashboard.
                </h1>
                <p className="text-muted-foreground mt-3">A quiet look at how CheerUp! is growing.</p>
            </motion.div>

            {/* Users stats */}
            <h2 className="font-heading text-sm font-[700] uppercase tracking-widest text-muted-foreground mb-4">Users</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <Stat icon={Users} label="Total users" value={stats.users.total} />
                <Stat icon={Users} label="New 24h" value={stats.users.new_24h} sub="last 24 hours" />
                <Stat icon={Users} label="New 7d" value={stats.users.new_7d} sub="last 7 days" />
                <Stat icon={Users} label="New 30d" value={stats.users.new_30d} sub="last 30 days" />
                <Stat icon={Sparkles} label="Google signins" value={stats.users.google} />
                <Stat icon={ShieldCheck} label="Email signins" value={stats.users.jwt} />
                <Stat icon={Crown} label="Admins" value={stats.users.admins} />
            </div>

            {/* Content stats */}
            <h2 className="font-heading text-sm font-[700] uppercase tracking-widest text-muted-foreground mb-4">Content</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <Stat icon={MessageCircleHeart} label="Chat messages" value={stats.content.chat_messages} />
                <Stat icon={MessageCircleHeart} label="Chat sessions" value={stats.content.chat_sessions} />
                <Stat icon={TrendingUp} label="Mood entries" value={stats.content.moods} />
                <Stat icon={BookHeart} label="Favorites" value={stats.content.favorites} />
            </div>

            {/* Signups chart */}
            {chartData.length >= 2 && (
                <div className="bg-card rounded-3xl p-8 border border-border shadow-soft mb-10" data-testid="admin-signup-chart">
                    <h2 className="font-heading text-lg font-[700] tracking-tight mb-6">Signups — last 30 days</h2>
                    <div className="h-64 min-h-[256px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(35 20% 88%)" />
                                <XAxis dataKey="date" stroke="hsl(20 10% 50%)" fontSize={11} />
                                <YAxis allowDecimals={false} stroke="hsl(20 10% 50%)" fontSize={11} />
                                <Tooltip contentStyle={{ background: "hsl(0 0% 100%)", border: "1px solid hsl(35 20% 85%)", borderRadius: 12 }} />
                                <Line type="monotone" dataKey="signups" stroke="hsl(18 70% 60%)" strokeWidth={3} dot={{ r: 4, fill: "hsl(18 70% 60%)" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Recent users table */}
            <div className="bg-card rounded-3xl p-8 border border-border shadow-soft" data-testid="admin-users-table">
                <h2 className="font-heading text-lg font-[700] tracking-tight mb-6">Recent users</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                                <th className="pb-4 font-medium">Name</th>
                                <th className="pb-4 font-medium">Email</th>
                                <th className="pb-4 font-medium">Provider</th>
                                <th className="pb-4 font-medium">Joined</th>
                                <th className="pb-4 font-medium">Role</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {users.map(u => (
                                <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                                    <td className="py-3 pr-4 font-medium">{u.name || "—"}</td>
                                    <td className="py-3 pr-4 text-muted-foreground truncate max-w-[240px]">{u.email}</td>
                                    <td className="py-3 pr-4"><span className="text-xs px-2 py-1 rounded-full bg-accent">{u.provider || "jwt"}</span></td>
                                    <td className="py-3 pr-4 text-muted-foreground">{new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</td>
                                    <td className="py-3">
                                        {u.is_admin ? (
                                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/15 text-primary">
                                                <Crown className="w-3 h-3" strokeWidth={2} /> admin
                                            </span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">member</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <FeedbackFooter />
        </div>
    );
}
